import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../services/analytics';
import '../styles/PublicationTimeline.css';

function PublicationTimeline() {
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);

  useEffect(() => {
    let ignore = false;

    fetch('/api/books/year-distribution')
      .then((response) => (response.ok ? response.json() : { years: [] }))
      .then((data) => {
        if (!ignore) {
          setYears(Array.isArray(data.years) ? data.years : []);
        }
      })
      .catch(() => {
        if (!ignore) {
          setYears([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  const maxTotal = years.reduce((max, entry) => Math.max(max, entry.total), 1);
  // Log scale prevents a single dominant modern year (e.g. 800 new titles in 2015) from making
  // every pre-1900 bar invisible at the linear-scale minimum. With log scale a year with 1 book
  // still renders a clearly visible bar (~12 px) and relative sizes remain meaningful.
  const logBarHeight = (total) => {
    const h = Math.round((Math.log(total + 1) / Math.log(maxTotal + 1)) * 120);
    return Math.max(8, h);
  };

  const handleYearClick = (year) => {
    trackEvent('publication_timeline_year_clicked', { year });
    sessionStorage.setItem('advancedSearchStateV2', JSON.stringify({
      expression: { type: 'group', children: [{ type: 'term', field: 'title', value: '' }] },
      filters: { yearFrom: String(year), yearTo: String(year), itemType: '', matchMode: 'contains' },
      sortBy: 'newest',
      autoSearch: true,
    }));
    navigate('/advanced-search');
  };

  const scrollByAmount = (amount) => {
    trackRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (loading || years.length === 0) {
    return null;
  }

  return (
    <section className="pub-timeline" aria-label="Browse the collection by publication year">
      <div className="pub-timeline__header">
        <h2 className="pub-timeline__title">📅 Browse by Publication Year</h2>
        <div className="pub-timeline__nav">
          <button type="button" onClick={() => scrollByAmount(-400)} aria-label="Scroll earlier years">←</button>
          <button type="button" onClick={() => scrollByAmount(400)} aria-label="Scroll later years">→</button>
        </div>
      </div>
      <div className="pub-timeline__track" ref={trackRef}>
        {years.map((entry) => (
          <button
            type="button"
            key={entry.year}
            className="pub-timeline__bar-wrap"
            onClick={() => handleYearClick(entry.year)}
            title={`${entry.total} title(s) published in ${entry.year}`}
          >
            <span
              className="pub-timeline__bar"
              style={{ height: `${logBarHeight(entry.total)}px` }}
            />
            <span className="pub-timeline__count">{entry.total}</span>
            <span className="pub-timeline__year">{entry.year}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default PublicationTimeline;
